/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
// tslint:disable: max-line-length
import { FinitePromiseQueue, FinitePromiseQueueError, getDeliveryOptionsForSelectedVariant, GetDeliveryOptionsForSelectedVariantInput, getDimensionsForSelectedVariant, GetDimensionsForSelectedVariantInput, getPriceForSelectedVariant, getProductAvailabilitiesForSelectedVariant, getProductPageUrlSync, getSelectedVariant, IProductInventoryInformation, IPromiseQueue, ObjectExtensions, PriceForSelectedVariantInput, ProductAvailabilitiesForSelectedVariantInput,SelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject, IComponentNodeProps, IModuleProps, IncrementalQuantity, INodeProps, ISingleSlideCarouselProps, isMobile, ITelemetryContent, KeyCodes, Modal, ModalBody, ModalFooter, ModalHeader, NodeTag, onTelemetryClick, SingleSlideCarousel, TelemetryConstant, VariantType } from '@msdyn365-commerce-modules/utilities';
import { ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import { Carousel, ICarouselProps } from '@msdyn365-commerce/components';
import { IImageData, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ProductDeliveryOptions, ProductDimensionValue, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ReleasedProductType } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import React from 'react';
import { getBuyboxAddToCart, getBuyBoxInventoryLabel, getBuyboxKeyInPrice, getBuyboxProductAddToWishlist, getBuyboxProductDescription, getBuyboxProductPrice, getBuyboxProductRating, getBuyboxProductTitle, getQuantityLimitsMessages, IBuyboxAddToCartViewProps, IBuyboxAddToOrderTemplateViewProps, IBuyboxAddToWishlistViewProps, IBuyboxCallbacks, IBuyboxCommonData, IBuyboxExtentedProps, IBuyboxKeyInPriceViewProps, IBuyboxProductConfigureViewProps, IBuyboxProductQuantityViewProps, IBuyboxState, IErrorState, IProductDetails, RetailDefaultOrderQuantityLimitsFeatureName } from '../../common';
import { getBuyboxProductConfigure } from '../../common/buybox-product-configure';
import { IQuickviewData } from './quickview.data';
import { IQuickviewProps, IQuickviewResources } from './quickview.props.autogenerated';
import { getProductDetails } from './utilities/get-product-details';
import { getValidProductImages } from './utilities/get-valid-product-images';

export interface IQuickviewViewProps extends IQuickviewProps<IQuickviewData> {
    quickView: IModuleProps;
    quickViewButton: React.ReactNode;
    ModalContainer: IModuleProps;
    ModalHeaderContainer: INodeProps;
    ModalHeaderContent: React.ReactNode;
    ModalFooterContainer:INodeProps;
    ModalBodyContainer: INodeProps;
    state: IBuyboxState;
    cartContainerProps: INodeProps;
    ModuleProps?: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks?: IBuyboxCallbacks;
    title?: React.ReactNode;
    description?: React.ReactNode;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    addToCart?: IBuyboxAddToCartViewProps;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    max: number | undefined;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    quantityLimitsMessages?: React.ReactNode;
    CarouselProps: INodeProps;
    Thumbnails: IQuickViewThumbnailsViewProps;
    seeDetailsbutton: React.ReactNode;
    isModalOpen: boolean;
    loading?: React.ReactNode;
}

export interface IQuickviewExtentedProps<T> extends IQuickviewProps<IQuickviewData> {
    selectedProductId?: number;
}

export interface IQuickViewThumbnailsViewProps {
    ThumbnailsContainerProps: INodeProps;
    SingleSlideCarouselComponentProps: INodeProps;
    items?: IQuickViewThumbnailItemViewProps[];
}

export interface IQuickViewThumbnailItemViewProps {
    ThumbnailItemContainerProps: INodeProps;
    Picture: React.ReactElement;
}

/**
 *
 * Quickview component
 * @extends {React.Component<IQuickviewProps<IQuickviewData>>}
 */
class Quickview extends React.Component<IQuickviewExtentedProps<IQuickviewData>, IBuyboxState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);
    private dimensions: { [id: number]: string } = {};
    private telemetryContent: ITelemetryContent;
    private productDetails: IProductDetails | undefined;
    private quickViewCallbacks: IBuyboxCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState: errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        dimensionSelectedAsync: (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            return this.dimensionUpdateQueue.enqueue(() => {
                return this._updateDimensions();
            }).catch((reason: any) => { // tslint:disable-line:no-any
                // Ignore discarded processes.
                if (reason !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                    throw reason;
                }
            });
        },
        getDropdownName: (dimensionType: number, resources: IQuickviewResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this._hideQuickViewDialog();
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension: isUpdatingDimension });
        },
        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState: errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        }
    };
    private readonly defaultGalleryImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=767&h=767&m=8', w: 0, h: 0 },
            sm: { q: 'w=600&h=600&m=8', w: 0, h: 0 },
            md: { q: 'w=600&h=772&m=8', w: 0, h: 0 },
            lg: { q: 'h=772&m=8', w: 0, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    private readonly defaultThumbnailImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=100&m=8', w: 100, h: 0 },
            lg: { q: 'w=100&m=8', w: 100, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    // private telemetryContent: ITelemetryContent;

    constructor(props: IQuickviewProps<IQuickviewData>) {
        super(props);
        this.state = {
            modalOpen: false,
            quantity: 1,
            min: undefined,
            max: undefined,
            errorState: {
                configureErrors: {}
            },
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,
            isUpdatingDimension: false,
            activeIndex: 0,
            animating: false,
            mediaGalleryItems: undefined,
        };

        this._quickViewCloseButtonclick = this._quickViewCloseButtonclick.bind(this);
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    public render(): JSX.Element | null {
        const {
            config: { className = '' },
            resources,
        } = this.props;
        const { max } = this.state;
        const product = this.productDetails?.product ? this.productDetails.product : undefined;
        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;
        const isMobileDevice = isMobile({variant: VariantType.Viewport, context: this.props.context.request}) === 'xs';
        this._updateMediaItems();
        const viewprops: IQuickviewViewProps = {
            ...(this.props as IQuickviewExtentedProps<IQuickviewData>),
            state: this.state,
            isModalOpen: this.state.modalOpen!,
            quickView: {
                moduleProps: this.props,
                className: classnames('ms-quickView', className),
                tag: 'div'
            },
            quickViewButton: !isMobileDevice && (
                <button className='ms-quickView__button' onClick={this.onClickHandler} aria-label={resources.quickViewbuttonText}>
                    {resources.quickViewbuttonText}
                </button>
            ),
            ModalContainer: {
                moduleProps: this.props,
                tag: Modal,
                className: 'ms-quickView__dialog',
                isOpen: this.state.modalOpen,
                toggle: this._quickViewCloseButtonclick,
                modalTransition: { timeout: 0 }
            },
            ModalHeaderContainer: {
                tag: ModalHeader,
                className: 'ms-quickView__header',
                toggle: this._quickViewCloseButtonclick
            },
            ModalFooterContainer: {
                tag: ModalFooter,
                className: 'ms-quickView__footer',
            },
            ModalHeaderContent: '',
            ModalBodyContainer: {
                tag: ModalBody,
                className: 'ms-quickView__body'
            },
            cartContainerProps: {
                className: 'ms-quickView__product-add-to-cart'
            },
            ProductInfoContainerProps: {
                className: 'ms-quickView__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-quickView__media-gallery'
            },
            seeDetailsbutton: this._renderSeeDetailButton(product),
            loading: !product && <div className='ms-quickView__loading__icon' />,
            callbacks: this.quickViewCallbacks,
            title: product && getBuyboxProductTitle(props, this.productDetails),
            description: product && getBuyboxProductDescription(props, this.productDetails),
            configure: product && getBuyboxProductConfigure(props, this.state, this.quickViewCallbacks, this.productDetails),
            price: product && getBuyboxProductPrice(props, this.state),
            rating: product && !this.props.context.app.config.hideRating && getBuyboxProductRating(props, this.productDetails),
            addToCart:
                product &&
                getBuyboxAddToCart(props, this.state, this.quickViewCallbacks, defaultMinimumKeyInPrice, defaultMaximumKeyInPrice, this.productDetails),
            keyInPrice:
                product && this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(props, this.state, this.quickViewCallbacks)
                    : undefined,
            quantity: product?.IsGiftCard ? undefined : this._renderQuantity(this.props, this.state, this.quickViewCallbacks),
            inventoryLabel: product && getBuyBoxInventoryLabel(props, this.productDetails),
            addToWishlist: product && getBuyboxProductAddToWishlist(props, this.state, this.quickViewCallbacks, this.productDetails),
            quantityLimitsMessages: product && getQuantityLimitsMessages(props, this.state),
            max: max,
            CarouselProps: this._renderCarousel(),
            Thumbnails: this._renderThumbnails()
        };

        return this.props.renderView(viewprops) as React.ReactElement;
    }

    private _renderSeeDetailButton = (product: SimpleProduct | undefined): JSX.Element => {
        const { config } = this.props;
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.seeDetails, '');
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);

        return (
            <Button
                className='ms-quickView__seeDetailsbutton'
                onClick={onTelemetryClick(this.telemetryContent, payLoad, TelemetryConstant.seeDetails)}
                {...attributes}
                aria-label={config.seeDetailsLinkText}
                href={this._getProductURL(product)}
            >
                {config.seeDetailsLinkText}
            </Button>
        );
    }

    private _renderCarousel = (): INodeProps => {
        const { config, resources } = this.props;
        const mediaGalleryItems = this.state.mediaGalleryItems;
        const galleryImageSettings = config.galleryImageSettings;
        if (galleryImageSettings) {
            galleryImageSettings.cropFocalRegion = true;
        }
        return (
            {
                tag: Carousel,
                className: 'ms-media-gallery__carousel',
                items:
                    mediaGalleryItems &&
                    mediaGalleryItems.map((item: IImageData, index: number) =>
                        this._renderCarouselItemImageView(item, galleryImageSettings || this.defaultGalleryImageSettings, index)
                    ),
                activeIndex: this.state.activeIndex,
                next: this.next,
                previous: this.previous,
                interval: false,
                directionTextPrev: resources.previousScreenshotFlipperText,
                directionTextNext: resources.nextScreenshotFlipperText,
                onIndicatorsClickHandler: this.goToIndex,
                handleOnExited: this.onExited,
                handleOnExiting: this.onExiting,
                key: this.state.lastUpdate
            } as IComponentNodeProps<ICarouselProps>
        );
    }

    private _renderThumbnails = (): IQuickViewThumbnailsViewProps => {
        const mediaGalleryItems = this.state.mediaGalleryItems;
        const { config, id, resources } = this.props;

        const thumbnailImageSettings = config.thumbnailImageSettings;
        if (thumbnailImageSettings) {
            thumbnailImageSettings.cropFocalRegion = true;
        }
        return (
            {
                ThumbnailsContainerProps: { className: 'ms-media-gallery__thumbnails-container' },
                SingleSlideCarouselComponentProps: {
                    tag: SingleSlideCarousel,
                    className: 'ms-media-gallery__thumbnails',
                    flipperPrevLabel: resources.previousScreenshotFlipperText,
                    flipperNextLabel: resources.nextScreenshotFlipperText,
                    parentId: id,
                    useTabList: true,
                    key: this.state.lastUpdate
                } as IComponentNodeProps<ISingleSlideCarouselProps>,
                items:
                    mediaGalleryItems &&
                    mediaGalleryItems.map((item: IImageData, index: number) =>
                        this._getThumbnailItem(
                            item,
                            thumbnailImageSettings || this.defaultThumbnailImageSettings,
                            index,
                            this.state.activeIndex!
                        )
                    )
            }
        );
    }

    private _updateMediaItems(): void {
        const product = this.productDetails?.product;
        if (!this.state.mediaGalleryItems && product) {
            this.setState({ mediaGalleryItems: [this._mapProductToImageData(product) || []] });
        }
    }

    private _updatePrice(
        newPrice: ProductPrice | undefined,
        customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    // tslint:disable-next-line:max-func-body-length
    private _updateDimensions = async (): Promise<void> => {
        const {
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const { product, productDimensions } = this.productDetails!;

        if (!product || !productDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[key]) {
                const errorState = { ...this.state.errorState };
                errorState.configureErrors[key] = undefined;

                this.setState({ errorState: errorState });
            }
        }

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions.map(dimension => {
            return {
                DimensionTypeValue: dimension.DimensionTypeValue,
                DimensionValue: this._updateDimensionValue(dimension, dimensionsToUpdate[dimension.DimensionTypeValue]) || dimension.DimensionValue,
                ExtensionProperties: dimension.ExtensionProperties
            };
        }).filter(dimension => {
            return dimension && dimension.DimensionValue;
        });
        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProduct = new Promise<SimpleProduct | null>(async (resolve) => {
            const newProduct = (await getSelectedVariant(
                new SelectedVariantInput(
                    product.MasterProductId ? product.MasterProductId : product.RecordId,
                    channelId,
                    mappedDimensions
                ),
                actionContext
            ));
            if (newProduct) {
                // @ts-ignore
                this.productDetails.productDimensions = await (getDimensionsForSelectedVariant(
                    new GetDimensionsForSelectedVariantInput(
                        newProduct.MasterProductId ? newProduct.MasterProductId : newProduct.RecordId,
                        channelId,
                        mappedDimensions
                    ),
                    actionContext
                ));
            }

            resolve(newProduct);
        });

        this.setState({ selectedProduct: selectedProduct });
        const variantProduct = await selectedProduct;

        if (variantProduct && this.productDetails) {
            // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
            // so that places like add to cart can await it
            this.productDetails.product = variantProduct;
            const images = await getValidProductImages(
                this.productDetails?.product.RecordId || 0,
                +this.props.context.request.apiSettings.channelId,
                this.props.context.actionContext,
                this.props.config.thumbnailImageSettings || this.defaultThumbnailImageSettings
            );
            this.setState({
                mediaGalleryItems: [...images],
                activeIndex: 0,
                lastUpdate: Date.now()
            });

            const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
                new ProductAvailabilitiesForSelectedVariantInput(
                    variantProduct.RecordId,
                    channelId
                ),
                actionContext
            );

            this.productDetails.productAvailableQuantity = newAvailableQuantity!;

            const isCustompriceSelected = variantProduct && variantProduct.Dimensions && variantProduct.Dimensions.find(dimension => dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value === 'Custom');
            if (isCustompriceSelected) {
                this.setState({ isCustomPriceSelected: true });
            } else {
                // Remove custom amount error when unselect the custom amount
                const errorState = { ...this.state.errorState };
                errorState.customAmountError = undefined;

                this.setState({ isCustomPriceSelected: false, isPriceKeyedIn: false, errorState: errorState });
            }

            if (newAvailableQuantity && newAvailableQuantity.length) {
                this.setState({ productAvailableQuantity: newAvailableQuantity[0] });
            } else {
                this.setState({ productAvailableQuantity: undefined });
            }

            // Step 6. Use these dimensions hydrate the product price.
            const newPrice = await getPriceForSelectedVariant(
                new PriceForSelectedVariantInput(
                    variantProduct.RecordId,
                    channelId
                ),
                actionContext
            );

            if (newPrice) {
                this._updatePrice(newPrice);
            }

            const RetailMulitplePickupMFeatureState = this.props.data.featureState.result?.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature');
            // Step 7. Use these dimensions hydrate the product delivery options.
            const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
                new GetDeliveryOptionsForSelectedVariantInput(
                    variantProduct.RecordId,
                    channelId,
                    undefined,
                    undefined,
                    RetailMulitplePickupMFeatureState?.IsEnabled
                ),
                actionContext
            );

            if (newDeliveryOptions) {
                this.setState({ productDeliveryOptions: newDeliveryOptions });
            }

            this.setState({ isUpdatingDimension: false });
            await this._updateQuantitiesInState(variantProduct);
        }
    }

    private _getProductURL(product: SimpleProduct | undefined): string {
        if (product) {
            let producturl = ' ';
            producturl = getProductPageUrlSync(product.Name || '', product.RecordId, this.props.context && this.props.context.actionContext, undefined);
            const canonicalurl = this.props.context.request.canonicalUrl;
            if (producturl && canonicalurl) {
                producturl = `${canonicalurl}${producturl}`;
            }
            return producturl;
        }
        return '';
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
        if (isOrderQuantityLimitsFeatureEnabled && product) {
            this.setState({
                quantity: product.Behavior?.DefaultQuantity || 1,
                min: product.Behavior?.MinimumQuantity || 1,
                max: (product.Behavior?.MaximumQuantity && product.Behavior?.MaximumQuantity > 0)
                    ? product.Behavior?.MaximumQuantity
                    : Number.MAX_VALUE,
            });
        } else {
            this.setState({
                min: 1,
                max: this.props.context.app.config.maxQuantityForCartLineItem || 10,
            });
        }
    }
    private _updateDimensionValue = (productDimensionFull: ProductDimensionFull, newValueId: string | undefined): ProductDimensionValue | undefined => {
        if (newValueId && productDimensionFull.DimensionValues) {
            return productDimensionFull.DimensionValues.find(dimension => dimension.RecordId === +newValueId);
        }

        return undefined;
    };

    private _getDropdownName = (dimensionType: number, resources: IQuickviewResources): string => {
        const isGiftCard = this.productDetails?.product?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };
    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        const featureStatuses = await this.props.data.featureState;
        const isFeatureEnabledInHq = featureStatuses?.find(featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName)?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        let customerInfo;
        try {
            customerInfo = await this.props.data.customerInformation;
        } catch (error) {
            this.props.telemetry.information(error);
            this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
            return false;
        }

        return customerInfo !== undefined &&
            ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b));
    }
    private onExiting = () => {
        this.setState({ animating: true });
    };

    private onExited = () => {
        this.setState({ animating: false });
    };

    private next = (): void => {
        if (this.isLastItem() === undefined) {
            return;
        }

        const nextIndex = this.isLastItem() ? 0 : this.state.activeIndex! + 1;
        this.goToIndex(nextIndex);
    };

    private previous = (): void => {
        const nextIndex = this.isFirstItem()
            ? this.state.mediaGalleryItems
                ? this.state.mediaGalleryItems.length - 1
                : 0
            : this.state.activeIndex! - 1;
        this.goToIndex(nextIndex);
    };

    private goToIndex = (index: number): void => {
        this.setState({ activeIndex: index });
    };

    private _renderCarouselItemImageView(image: IImageData, imageSettings: IImageSettings, index: number): React.ReactNode {
        return <React.Fragment>{this._getCarouselItem(image, imageSettings, index)}</React.Fragment>;
    }

    private _getCarouselItem = (image: IImageData, imageSettings: IImageSettings, index: number): React.ReactNode => (
        <Image
            className='ms-media-gallery__item'
            {...image}
            gridSettings={this.props.context.request.gridSettings!}
            imageSettings={imageSettings}
            loadFailureBehavior='default'
            role='tabpanel'
            id={`${this.props.id}__carousel-item__${index}`}
        />
    );

    private _getThumbnailItem = (image: IImageData, imageSettings: IImageSettings, index: number, modifiedActiveIndex: number): IQuickViewThumbnailItemViewProps => {
        const classes = classnames(
            'ms-media-gallery__thumbnail-item',
            modifiedActiveIndex === index ? 'ms-media-gallery__thumbnail-item-active' : ''
        );

        return {
            ThumbnailItemContainerProps: {
                tag: 'li' as NodeTag,
                className: classes,
                role: 'tab',
                tabIndex: 0,
                key: index,
                'aria-label': image.altText,
                'aria-selected': modifiedActiveIndex === index,
                'aria-controls': `${this.props.id}__carousel-item__${index}`,
                onClick: this._generateOnThumbnailClick(index),
                onKeyDown: this._generateOnThumbnailKeyDown(index)
            },
            Picture: (
                <Image
                    className='ms-media-gallery__thumbnail'
                    {...image}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='default'
                />
            )
        };
    };

    private _generateOnThumbnailKeyDown = (index: number) => {
        return (event: React.KeyboardEvent) => {
            if (event.which === KeyCodes.Enter || event.which === KeyCodes.Space) {
                event.preventDefault();

                this.goToIndex(index);
            }
        };
    };

    private _generateOnThumbnailClick = (index: number) => {
        return (event: React.MouseEvent<HTMLLIElement>) => {
            event.preventDefault();

            this.goToIndex(index);
        };
    };
    private _mapProductToImageData(product: SimpleProduct): IImageData {
        return {
            src: product.PrimaryImageUrl || ''
        };
    }

    private isFirstItem = () => this.state.activeIndex === 0;

    private isLastItem = () => this.state.mediaGalleryItems && this.state.activeIndex === this.state.mediaGalleryItems.length - 1;

    private onClickHandler = (event: React.MouseEvent<HTMLElement>) => { return this._onClick(event, this.props); };

    private _onClick = async (_event: React.MouseEvent<HTMLElement>, props: IQuickviewExtentedProps<IQuickviewData>) => {
        if (!ObjectExtensions.isNullOrUndefined(props.selectedProductId)) {
            const {
                context: {
                    actionContext: actionContext,
                    request: {
                        apiSettings: { channelId }
                    }
                },
            } = this.props;

            if (this.props.selectedProductId) {
                this.setState({ modalOpen: true });
                const productId = this.props.selectedProductId;
                this.productDetails = await getProductDetails(productId, channelId, actionContext);
                const {
                    product,
                    productPrice
                } = this.productDetails;
                if (productPrice) {
                    this._updatePrice(productPrice);
                }

                if (product) {
                    // check if the product is service or not by product type
                    const PRODUCTASSERVICE = 2 as ReleasedProductType.Service;
                    if (product.ItemTypeValue === PRODUCTASSERVICE) {
                        this.setState({ isServiceItem: true });
                    }
                    await this._updateQuantitiesInState(product);
                    const images = await getValidProductImages(
                        product.RecordId,
                        +this.props.context.request.apiSettings.channelId,
                        this.props.context.actionContext,
                        this.props.config.thumbnailImageSettings || this.defaultThumbnailImageSettings
                    );
                    this.setState({
                        mediaGalleryItems: [...images],
                        activeIndex: 0,
                        lastUpdate: Date.now()
                    });
                }
            }
        }
    };

    private _quickViewCloseButtonclick = () => {
        this._hideQuickViewDialog();
    };

    private _hideQuickViewDialog(): void {
        this.dimensions = [];
        this.setState({
            modalOpen: false,
            quantity: 1,
            min: undefined,
            max: undefined,
            errorState: {
                configureErrors: {}
            },
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,
            isUpdatingDimension: false,
            productAvailableQuantity: undefined,
            lastUpdate: undefined,
            activeIndex: undefined,
            animating: undefined,
            mediaGalleryItems: undefined
        });

    }

    private _renderQuantity = (props: IQuickviewExtentedProps<IQuickviewData>, state: IBuyboxState, callbacks: IBuyboxCallbacks): IBuyboxProductQuantityViewProps => {

        const {
            resources
        } = props;

        const
        {
            quantity,
            max,
            errorState: {
                quantityError,
            }
        } = state;

        const onChange = (newValue: number): boolean => {
            if (callbacks.updateQuantity) {
                return callbacks.updateQuantity(newValue);
            }
            return true;
        };

        return {
            ContainerProps: {
                className: `ms-quickView__quantity`
            },
            LabelContainerProps: {
                tag: 'label',
                className: `ms-quickView__product-quantity-label`,
                htmlFor: `ms-quickView__product-quantity-input`
            },
            heading: (
                <div className={`ms-quickView__product-quantity-label-heading`}>{resources.productQuantityHeading}</div>
            ),
            errors: quantityError && (
                <span className='msc-alert msc-alert-noborder msc-alert-danger'>
                    <span className='msi-exclamation-triangle' aria-hidden='true' />
                    <span>{quantityError}</span>
                </span>
            ),
            input: (
                <IncrementalQuantity
                    id='ms-buybox__product-quantity-input'
                    max={max}
                    currentCount={quantity}
                    onChange={onChange}
                    inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                    decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                    incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                    minQuantityText={resources.minQuantityText}
                    maxQuantityText={resources.maxQuantityText}
                    telemetryContent={this.telemetryContent}
                    disabled={this.state.isUpdatingDimension}
                />
            )
        };
    };
}
export default Quickview;
